import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/alert.default.js';
import * as wireframeSkin from './skins/alert.wireframe.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

const VALID_TYPES = ['info', 'success', 'warning', 'error'];
const DEFAULT_TYPE = 'info';

class RunAlert extends RunElement {
  #type = DEFAULT_TYPE;
  #dismissible = false;
  #dismissed = false;
  #rendering = false;

  static get observedAttributes() {
    return ['type', 'dismissible', 'dismissed'];
  }

  _onKeydownBound = (e) => this.#handleKeydown(e);
  _onDismissClickBound = (e) => this.#handleDismissClick(e);

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'alert');
    this.addEventListener('keydown', this._onKeydownBound);
    this.shadowRoot.addEventListener('click', this._onDismissClickBound);
    this.#updateAria();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this._onKeydownBound);
    this.shadowRoot.removeEventListener('click', this._onDismissClickBound);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'type':        this.type = newValue; break;
      case 'dismissible': this.dismissible = newValue !== null; break;
      case 'dismissed':   this.dismissed = newValue !== null; break;
    }
  }

  get type() { return this.#type; }
  set type(value) {
    const coerced = String(value ?? '').trim().toLowerCase();
    const resolved = VALID_TYPES.includes(coerced) ? coerced : DEFAULT_TYPE;
    if (this.#type === resolved) return;
    this.#type = resolved;
    this.setAttribute('type', resolved);
    this.#updateAria();
    this.requestUpdate();
  }

  get dismissible() { return this.#dismissible; }
  set dismissible(value) {
    const coerced = Boolean(value);
    if (this.#dismissible === coerced) return;
    this.#dismissible = coerced;
    this.toggleAttribute('dismissible', coerced);
    this.#updateAria();
    this.requestUpdate();
  }

  get dismissed() { return this.#dismissed; }
  set dismissed(value) {
    const coerced = Boolean(value);
    if (this.#dismissed === coerced) return;
    this.#dismissed = coerced;
    this.toggleAttribute('dismissed', coerced);
    this.#updateAria();
    this.requestUpdate();
  }

  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
    } finally {
      this.#rendering = false;
    }
    this.#updateAria();
  }

  #resolveSkin() {
    const s = this.getAttribute('skin');
    if (s) return s;
    return resolveSkinFromAncestors(this);
  }

  #updateAria() {
    this.setAttribute('aria-live', this.#type === 'error' || this.#type === 'warning' ? 'assertive' : 'polite');
    this.setAttribute('aria-atomic', 'true');
    this.setAttribute('aria-hidden', String(this.#dismissed));
    if (this.#dismissible) {
      this.setAttribute('tabindex', '0');
    } else {
      this.removeAttribute('tabindex');
    }
  }

  #handleKeydown(event) {
    if (!this.#dismissible || this.#dismissed) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.#dismiss();
    }
  }

  #handleDismissClick(event) {
    if (!this.#dismissible || this.#dismissed) return;
    const target = event.target;
    if (target && target.closest('[part="dismiss"]')) {
      this.#dismiss();
    }
  }

  #dismiss() {
    this.dismissed = true;
    this.#emitDismiss();
  }

  #emitDismiss() {
    this.dispatchEvent(new CustomEvent('dismiss', {
      bubbles: true,
      composed: true,
      detail: { type: this.#type },
    }));
  }
}

if (!customElements.get('run-alert')) {
  customElements.define('run-alert', RunAlert);
}

export { RunAlert };
export default RunAlert;